import { Match, Hero, ItemData, Player } from '../types/types';
import { getDatabase, ref, get, set, onValue, off } from 'firebase/database';

// Use proxy in development to avoid CORS issues
const OPENDOTA_API = import.meta.env.DEV
  ? '/api/opendota'
  : 'https://api.opendota.com/api';

// Session type definition
interface SessionData {
  matchId: string;
  createdAt: string;
  sessionId: string;
  items?: Record<string, any>;
}

export class DotaService {
  static async getMatch(matchId: string): Promise<Match> {
    try {
      const response = await fetch(`${OPENDOTA_API}/matches/${matchId}`);
      if (!response.ok) throw new Error('Failed to fetch match');
      const data = await response.json();
      console.log('Match data:', data); // Debug log
      
      // Get purchase logs from detailed match data if not included in basic data
      if (data.players && data.players.some((p: Player) => !p.purchase_log || p.purchase_log.length === 0)) {
        console.log('Purchase logs missing, fetching detailed data...');
        await DotaService.enrichPlayerPurchaseLogs(data);
      }
      
      // Extract team information if available
      if (data.radiant_team && !data.radiant_team.name) {
        data.radiant_team.name = data.radiant_team.tag || 'Radiant';
      }
      
      if (data.dire_team && !data.dire_team.name) {
        data.dire_team.name = data.dire_team.tag || 'Dire';
      }
      
      // Add team names if they're missing completely
      if (!data.radiant_team && !data.radiant_name) {
        data.radiant_name = 'Radiant';
      }
      
      if (!data.dire_team && !data.dire_name) {
        data.dire_name = 'Dire';
      }
      
      return data;
    } catch (error) {
      console.error('Error fetching match:', error);
      throw error;
    }
  }
  
  // New function to specifically fetch purchase logs
  static async enrichPlayerPurchaseLogs(match: Match): Promise<void> {
    try {
      // Some versions of the API don't include purchase_log in the main match data
      // We need to manually fetch it for each player
      if (!match.players) return;
      
      const promises = match.players.map(async (player) => {
        try {
          if (player.purchase_log && player.purchase_log.length > 0) {
            console.log(`Player ${player.hero_id} already has purchase log`);
            return;
          }
          
          // Try to get from player matches endpoint
          console.log(`Fetching purchase log for player ${player.hero_id}...`);
          const response = await fetch(`${OPENDOTA_API}/players/${player.account_id}/matches?limit=1&game_mode=1&significant=0&project[]=purchase_log&project[]=purchase`);
          if (!response.ok) return;
          
          const matches = await response.json();
          const recentMatch = matches.find((m: any) => m.match_id.toString() === match.match_id?.toString());
          
          if (recentMatch && recentMatch.purchase_log) {
            console.log(`Found purchase log for player ${player.hero_id}:`, recentMatch.purchase_log);
            player.purchase_log = recentMatch.purchase_log;
          } else {
            console.log(`No purchase log found for player ${player.hero_id} in recent matches`);
            // Create a synthetic purchase log from purchases object if available
            if (player.purchase && typeof player.purchase === 'object') {
              player.purchase_log = [];
              Object.entries(player.purchase).forEach(([item, count]: [string, any]) => {
                if (count && typeof count === 'number') {
                  // Distribute purchases throughout the game at reasonable intervals
                  for (let i = 0; i < count; i++) {
                    // Estimate time based on item index (just for demo purposes)
                    const estimatedTime = Math.min(600, 300 + i * 200 + Math.random() * 120);
                    if (player.purchase_log) {
                      player.purchase_log.push({
                        time: Math.floor(estimatedTime),
                        item_id: parseInt(item) || 0 // Convert to number if possible
                      });
                    }
                  }
                }
              });
              console.log(`Created synthetic purchase log for player ${player.hero_id}:`, player.purchase_log);
            }
          }
        } catch (err) {
          console.error(`Error fetching purchase log for player ${player.hero_id}:`, err);
        }
      });
      
      await Promise.all(promises);
    } catch (error) {
      console.error('Error enriching player purchase logs:', error);
    }
  }

  static async getHeroes(): Promise<Hero[]> {
    try {
      const response = await fetch(`${OPENDOTA_API}/heroes`);
      if (!response.ok) throw new Error('Failed to fetch heroes');
      const data = await response.json();
      console.log('Raw hero data:', data); // Debug log

      const heroes = data.map((hero: any) => ({
        id: hero.id,
        name: hero.localized_name,
        imageUrl: `https://cdn.cloudflare.steamstatic.com/apps/dota2/images/heroes/${hero.name.split('npc_dota_hero_')[1]}_full.png`
      }));
      
      console.log('Processed heroes:', heroes.map((h: Hero) => ({ id: h.id, name: h.name })));
      return heroes;
    } catch (error) {
      console.error('Error in getHeroes:', error);
      throw error;
    }
  }

  static async getItems(): Promise<Record<number, ItemData>> {
    try {
      // Try the fallback API if OpenDota fails
      const apiURLs = [
        'https://api.opendota.com/api/constants/items',
        'https://raw.githubusercontent.com/odota/dotaconstants/master/build/items.json'
      ];

      let data;
      for (const url of apiURLs) {
        try {
          const response = await fetch(url);
          if (response.ok) {
            data = await response.json();
            break;
          }
        } catch (err) {
          console.warn(`Failed to fetch from ${url}`, err);
        }
      }

      if (!data) {
        throw new Error('Failed to fetch items from all sources');
      }

      console.log('Raw items data:', data);
      const processedItems: Record<number, ItemData> = {};

      // Process items without recipes and with valid data
      Object.entries(data).forEach(([key, item]: [string, any]) => {
        try {
          // Basic validation
          if (!item || !item.dname || !item.img) return;

          // Skip recipe items
          if (item.recipe === 1 ||
              key.includes('recipe') ||
              item.dname?.toLowerCase().includes('recipe')) {
            return;
          }

          // Get numeric ID
          const numericId = parseInt(item.id || key);
          if (isNaN(numericId)) return;

          // Process item - use key as name (e.g., 'quelling_blade')
          processedItems[numericId] = {
            id: numericId,
            img: item.img.startsWith('/') ? item.img : `/${item.img}`,
            dname: item.dname.trim(),
            cost: parseInt(item.cost) || 0,
            name: key, // Use the key as the name for matching
            qual: item.qual || ''
          };
        } catch (err) {
          console.warn(`Failed to process item ${key}:`, err);
        }
      });

      const itemCount = Object.keys(processedItems).length;
      console.log(`Processed ${itemCount} items successfully`);

      if (itemCount === 0) {
        throw new Error('No items could be processed');
      }

      return processedItems;
    } catch (error) {
      console.error('Failed to load items:', error);
      throw new Error('Failed to load item data');
    }
  }

  static async getMatchItemsFromSources(matchId: string): Promise<any> {
    try {
      // Try multiple sources to get item data
      const urls = [
        `${OPENDOTA_API}/matches/${matchId}`,
        `https://api.stratz.com/api/v1/match/${matchId}`,
        `https://api.opendota.com/api/request/${matchId}`
      ];
      
      for (const url of urls) {
        try {
          console.log(`Trying to fetch match data from ${url}`);
          const response = await fetch(url);
          if (response.ok) {
            const data = await response.json();
            if (data && data.players) {
              console.log(`Successfully got match data from ${url}`);
              return data;
            }
          }
        } catch (err) {
          console.warn(`Failed to fetch from ${url}`, err);
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error getting match data from all sources:', error);
      return null;
    }
  }

  // Get session data from Firebase by session ID
  static async getSessionData(sessionId: string): Promise<SessionData | null> {
    try {
      const db = getDatabase();
      const sessionRef = ref(db, `sessions/${sessionId}`);
      const snapshot = await get(sessionRef);
      
      if (snapshot.exists()) {
        return snapshot.val() as SessionData;
      } else {
        console.log(`No session found with ID: ${sessionId}`);
        return null;
      }
    } catch (error) {
      console.error('Error fetching session data:', error);
      return null;
    }
  }

  // Create a new session in Firebase
  static async createSession(sessionData: SessionData): Promise<boolean> {
    try {
      const db = getDatabase();
      const sessionRef = ref(db, `sessions/${sessionData.sessionId}`);
      await set(sessionRef, sessionData);
      console.log(`Session created with ID: ${sessionData.sessionId}`);
      return true;
    } catch (error) {
      console.error('Error creating session:', error);
      return false;
    }
  }

  // Update session data in Firebase - used for syncing changes
  static async updateSessionItems(sessionId: string, heroId: string | number, items: any): Promise<boolean> {
    try {
      const db = getDatabase();
      const itemsRef = ref(db, `sessions/${sessionId}/items/${heroId}`);
      await set(itemsRef, items);
      return true;
    } catch (error) {
      console.error('Error updating session items:', error);
      return false;
    }
  }

  // Update all items at once for a session - used for bulk updates
  static async updateAllSessionItems(sessionId: string, items: Record<string, any>): Promise<boolean> {
    try {
      const db = getDatabase();
      const itemsRef = ref(db, `sessions/${sessionId}/items`);
      await set(itemsRef, items);
      console.log(`Updated all items for session ${sessionId}`);
      return true;
    } catch (error) {
      console.error('Error updating all session items:', error);
      return false;
    }
  }

  // Listen for changes in a session's items
  static listenForSessionItems(sessionId: string, callback: (items: Record<string, any>) => void): () => void {
    const db = getDatabase();
    const itemsRef = ref(db, `sessions/${sessionId}/items`);
    
    onValue(itemsRef, (snapshot) => {
      if (snapshot.exists()) {
        callback(snapshot.val());
      }
    });
    
    // Return a function to unsubscribe from updates
    return () => off(itemsRef);
  }

  static processMatchObjectives(match: Match): Array<{
    time: number;
    type: string;
    team: string;
    teamName?: string;
    lane?: string;
    key?: string;
    slot?: number;
    player_slot?: number;
    unit?: string;
    team_id?: number;
    player1?: number;
    player2?: number;
    value?: number;
    tier?: number;
    tierName?: string;
    heroId?: number;
    victimId?: number;
    victimTeamName?: string;
  }> {
    const objectives: any[] = [];
    
    // Extract team names from match data if available
    const radiantTeamName = match.radiant_team?.name || match.radiant_name || 'Radiant';
    const direTeamName = match.dire_team?.name || match.dire_name || 'Dire';
    
    console.log(`Team names: Radiant="${radiantTeamName}", Dire="${direTeamName}"`);
    
    // Extract objectives from match data
    if (match.objectives && match.objectives.length > 0) {
      console.log('Processing match objectives:', match.objectives);
      
      // Map team number to team name
      const getTeamName = (teamNumber: number): string => {
        return teamNumber === 0 ? 'radiant' : 'dire';
      };
      
      // Get display team name
      const getDisplayTeamName = (teamNumber: number): string => {
        return teamNumber === 0 ? radiantTeamName : direTeamName;
      };
      
      // Process each objective
      match.objectives.forEach(objective => {
        // Convert basic objective data with proper typing
        const processedObjective: any = {
          time: objective.time,
          type: objective.type,
          team: getTeamName(objective.team),
          teamName: getDisplayTeamName(objective.team) // Add team name
        };
        
        // Process and fix type names
        if (processedObjective.type === 'CHAT_MESSAGE_COURIER_LOST') {
          processedObjective.type = 'CHAT_MESSAGE_COURIER_LOST';
        } else if (processedObjective.type === 'CHAT_MESSAGE_FIRSTBLOOD') {
          processedObjective.type = 'CHAT_MESSAGE_FIRSTBLOOD';
        } else if (processedObjective.type === 'ROSHAN_KILL' || processedObjective.type === 'CHAT_MESSAGE_ROSHAN_KILL') {
          // Fix type name consistency
          processedObjective.type = 'ROSHAN_KILL';
          
          // FIX: For Roshan kill, we need to check player_slot or other attributes
          // Roshan kill is typically attributed to the team that picked up the Aegis immediately after
          // So we'll look for an Aegis pickup event near this time to determine the correct team
          
          // First, check if the player slot is defined, which would directly tell us the team
          if (objective.player_slot !== undefined) {
            // In Dota 2, player_slot < 128 means Radiant team, >= 128 means Dire team
            const isRadiant = objective.player_slot < 128;
            
            // Override the team based on player_slot
            processedObjective.team = isRadiant ? 'radiant' : 'dire';
            processedObjective.teamName = isRadiant ? radiantTeamName : direTeamName;
          } else if (objective.slot !== undefined) {
            // Sometimes slot is used instead of player_slot
            const isRadiant = objective.slot < 5; // Slots 0-4 are Radiant, 5-9 are Dire
            
            // Override the team based on slot
            processedObjective.team = isRadiant ? 'radiant' : 'dire';
            processedObjective.teamName = isRadiant ? radiantTeamName : direTeamName;
          }
          
          // Store this objective to check for nearby Aegis pickups later
          // (We'll do this after we process all objectives)
        } else if (processedObjective.type === 'AEGIS' || processedObjective.type === 'aegis' || processedObjective.type === 'CHAT_MESSAGE_AEGIS') {
          // Fix type name consistency
          processedObjective.type = 'AEGIS';
          
          // FIX: For Aegis pickup, we need to check player_slot to determine the correct team
          if (objective.player_slot !== undefined) {
            // In Dota 2, player_slot < 128 means Radiant team, >= 128 means Dire team
            const isRadiant = objective.player_slot < 128;
            
            // Override the team based on player_slot
            processedObjective.team = isRadiant ? 'radiant' : 'dire';
            processedObjective.teamName = isRadiant ? radiantTeamName : direTeamName;
          } else if (objective.slot !== undefined) {
            // Sometimes slot is used instead of player_slot
            const isRadiant = objective.slot < 5; // Slots 0-4 are Radiant, 5-9 are Dire
            
            // Override the team based on slot
            processedObjective.team = isRadiant ? 'radiant' : 'dire';
            processedObjective.teamName = isRadiant ? radiantTeamName : direTeamName;
          } else if (objective.key && objective.key.includes('npc_dota_hero_')) {
            // Try to find the hero in the players list to determine team
            const heroName = objective.key.replace('npc_dota_hero_', '');
            const player = match.players?.find(p => {
              const playerHeroName = p.hero_id?.toString() || '';
              return playerHeroName.includes(heroName);
            });
            
            if (player) {
              const isRadiant = player.team_number === 0;
              processedObjective.team = isRadiant ? 'radiant' : 'dire';
              processedObjective.teamName = isRadiant ? radiantTeamName : direTeamName;
            }
          }
        } else if (processedObjective.type === 'building_kill') {
          // Convert building_kill to proper tower or barracks type
          if (objective.key && objective.key.includes('tower')) {
            processedObjective.type = 'TOWER_KILL';
          } else if (objective.key && objective.key.includes('rax')) {
            processedObjective.type = 'BARRACKS_KILL';
          } else if (objective.key && objective.key.includes('fort')) {
            processedObjective.type = 'ANCIENT_KILL';
          } else {
            processedObjective.type = 'BUILDING_KILL'; // Generic if we can't determine
          }
          
          // FIX: For building kills, we need to adjust the team based on the key
          if (objective.key) {
            // If key contains 'goodguys', it's a Radiant building (destroyed by Dire)
            // If key contains 'badguys', it's a Dire building (destroyed by Radiant)
            if (objective.key.includes('goodguys')) {
              // Radiant building was destroyed by Dire
              processedObjective.team = 'dire';
              processedObjective.teamName = direTeamName;
            } else if (objective.key.includes('badguys')) {
              // Dire building was destroyed by Radiant
              processedObjective.team = 'radiant';
              processedObjective.teamName = radiantTeamName;
            }
          }
        }
        
        // Copy any additional properties that might exist
        if (objective.key) processedObjective.key = objective.key;
        if (objective.slot) processedObjective.slot = objective.slot;
        if (objective.player_slot) processedObjective.player_slot = objective.player_slot;
        
        objectives.push(processedObjective);
      });
    } else {
      console.log('No objectives found in match data');
    }
    
    // Extract kills from kill logs if objectives don't include them
    if (match.players) {
      match.players.forEach(player => {
        // Process kill logs
        if (player.kills_log) {
          const isRadiant = player.team_number === 0;
          
          player.kills_log.forEach(kill => {
            objectives.push({
              time: kill.time,
              type: 'HERO_KILL',
              team: isRadiant ? 'radiant' : 'dire',
              heroId: player.hero_id,
              key: kill.key,
              // Use optional chaining to avoid errors if player_slot doesn't exist
              player_slot: (player as any).player_slot,
              value: 1 // Kill count
            });
          });
        }
        
        // Process death logs
        if (player.deaths_log) {
          const isRadiant = player.team_number === 0;
          
          player.deaths_log.forEach(death => {
            objectives.push({
              time: death.time,
              type: 'HERO_DEATH',
              team: isRadiant ? 'radiant' : 'dire',
              heroId: player.hero_id,
              key: death.key,
              // Use optional chaining to avoid errors if player_slot doesn't exist
              player_slot: (player as any).player_slot
            });
          });
        }
      });
    }
    
    // Add first blood event if it doesn't exist
    const hasFirstBlood = objectives.some(obj => obj.type === 'CHAT_MESSAGE_FIRSTBLOOD');
    if (!hasFirstBlood) {
      // Try to determine first blood from kill logs
      const kills = objectives.filter(obj => obj.type === 'HERO_KILL').sort((a, b) => a.time - b.time);
      if (kills.length > 0) {
        const firstKill = kills[0];
        objectives.push({
          time: firstKill.time,
          type: 'CHAT_MESSAGE_FIRSTBLOOD',
          team: firstKill.team,
          key: firstKill.key,
          heroId: firstKill.heroId
        });
      }
    }
    
    // Sort objectives by time
    objectives.sort((a, b) => a.time - b.time);
    
    // Second pass to correlate Roshan kill with Aegis pickup
    // Match Roshan kill events with nearby Aegis pickups to fix team attribution
    const roshanKills = objectives.filter(obj => obj.type === 'ROSHAN_KILL');
    const aegisPickups = objectives.filter(obj => obj.type === 'AEGIS');
    
    roshanKills.forEach(roshKill => {
      // Find the nearest Aegis pickup after this Roshan kill
      const nearbyAegis = aegisPickups.find(aegis => 
        aegis.time >= roshKill.time && 
        aegis.time <= roshKill.time + 60 // Within 60 seconds after Roshan kill
      );
      
      // If we found a nearby Aegis pickup, use its team for the Roshan kill
      if (nearbyAegis) {
        roshKill.team = nearbyAegis.team;
        roshKill.teamName = nearbyAegis.teamName;
      }
    });
    
    // Add more context to tower kills and barracks
    objectives.forEach(objective => {
      // For buildings, add the opposing team's name
      if (['TOWER_KILL', 'BARRACKS_KILL', 'ANCIENT_KILL', 'BUILDING_KILL'].includes(objective.type)) {
        // The team that owns the building (the victim) is the opposite of the team that destroyed it
        objective.victimTeamName = objective.team === 'radiant' ? direTeamName : radiantTeamName;
      }
      
      // Add tier and lane information for tower kills and general building kills
      if (objective.type === 'TOWER_KILL' || (objective.type === 'BUILDING_KILL' && objective.key && objective.key.includes('tower'))) {
        // Try to determine the tier and lane from unit or other properties
        if (objective.key) {
          const towerInfo = objective.key.match(/npc_dota_(good|bad)guys_tower(.*)/);
          if (towerInfo) {
            const keyParts = towerInfo[2].split('_');
            
            // Extract tier number
            objective.tier = parseInt(keyParts[0].replace(/\D/g, '')) || 0;
            
            // Map to standard tier naming
            if (objective.tier === 1) {
              objective.tierName = 'T1';
            } else if (objective.tier === 2) {
              objective.tierName = 'T2';
            } else if (objective.tier === 3) {
              objective.tierName = 'T3';
            } else if (objective.tier === 4) {
              objective.tierName = 'T4';
              objective.lane = 'base'; // T4 towers are in the base
            }
            
            if (keyParts.length > 1) {
              // Add lane information
              if (keyParts[1].includes('bot')) objective.lane = 'bottom';
              else if (keyParts[1].includes('mid')) objective.lane = 'middle';
              else if (keyParts[1].includes('top')) objective.lane = 'top';
            }
          }
        }
      }
      
      // Add lane information for barracks and general building kills
      if (objective.type === 'BARRACKS_KILL' || (objective.type === 'BUILDING_KILL' && objective.key && objective.key.includes('rax'))) {
        if (objective.key) {
          // Determine lane
          if (objective.key.includes('bot')) objective.lane = 'bottom';
          else if (objective.key.includes('mid')) objective.lane = 'middle';
          else if (objective.key.includes('top')) objective.lane = 'top';
          
          // Determine if melee or ranged
          if (objective.key.includes('melee')) {
            objective.unit = 'melee';
            objective.tierName = 'mega melee'; // For display
          } else if (objective.key.includes('range')) {
            objective.unit = 'ranged';
            objective.tierName = 'mega ranged'; // For display
          }
        }
      }
    });
    
    return objectives;
  }

  static async getMatchObjectives(matchId: string): Promise<Array<{
    time: number;
    type: string;
    team: string;
    teamName?: string;
    lane?: string;
    key?: string;
    slot?: number;
    player_slot?: number;
    unit?: string;
    team_id?: number;
    player1?: number;
    player2?: number;
    value?: number;
    tier?: number;
    tierName?: string;
    heroId?: number;
    victimId?: number;
  }>> {
    try {
      // Fetch match data
      const match = await DotaService.getMatch(matchId);
      
      // Process the objectives
      return DotaService.processMatchObjectives(match);
    } catch (error) {
      console.error('Error fetching match objectives:', error);
      return [];
    }
  }
}
